import { type ComponentType, type ReactNode } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { Main as UntypedMain, Page as UntypedPage } from '../../components';

/* The layout frame is design-system JavaScript built through `styled`, which leaves TypeScript with a
   component that declares no props at all — not even children. These name the frame props the Admin
   pages pass and nothing else. */
type LayoutTheme = {
  breakpoints: (values: Record<string, number | string>) => number | string;
  sizes: { appBarHeight: number };
  spacing: (units: number) => number;
};

type FrameProps = {
  children: ReactNode;
  layout?: string;
  maxWidth?: string;
  px?: (params: { theme: LayoutTheme }) => number | string;
  padTop?: (params: { theme: LayoutTheme }) => number | string;
  padBottom?: string;
};

const Page = UntypedPage as ComponentType<FrameProps>;
const Main = UntypedMain as ComponentType<FrameProps>;

/** The footprint the Admin pages share: full width under the app bar, kept off the edges. */
export const AdminFrame = ({ children }: { children: ReactNode }) => (
  <Page padTop={({ theme }) => theme.breakpoints({ xs: 0, sm: 0 })}>
    <Main
      /* Main caps a column layout at $mainWidth (920px); these pages are meant to fill the container,
         and the px breakpoints below keep them off the edges. */
      maxWidth="100%"
      px={({ theme }) => theme.breakpoints({ xs: '$2', sm: '$6' })}
      layout="top-left"
      padTop={({ theme }) => theme.sizes.appBarHeight + theme.spacing(1)}
      padBottom="$16"
    >
      {children}
    </Main>
  </Page>
);

/** The sentence every Admin view shows when what it asked for did not come back. */
export const LoadFailed = () => (
  <Alert variant="destructive" className="mt-4">
    <AlertDescription>Sorry something went wrong, please refresh or contact an administrator.</AlertDescription>
  </Alert>
);

export const Loading = () => (
  <div className="mt-4 flex w-full justify-center">
    <Spinner />
  </div>
);
